import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Dialog } from "../ui/Dialog";
import { useTranslation } from "../../features/i18n/I18nProvider";
import { classifyLink, type ClassifiedLink } from "../../features/links/linkKind";
import { logEvent } from "../../services/logger";
import "./LinkViewer.css";

/**
 * Opens a link from an answer INSIDE the app, in a window sized to what it is.
 *
 *   image    at its own size, capped to the screen
 *   youtube  a 16:9 player
 *   pdf      a tall reading window
 *   page     a large window, with "Open in new tab" always one click away
 *
 * ONE LISTENER FOR THE WHOLE APP. Any `<a data-link-viewer>` - written by the
 * markdown renderer, so chat, library and research answers all get it - opens
 * here. A modified click (Ctrl/Cmd/Shift, middle button) is left alone, so
 * "open in a new tab" still works the way every browser user expects.
 *
 * WHAT IT CANNOT DO, AND SAYS SO. Many sites forbid being shown inside another
 * site (X-Frame-Options / frame-ancestors). A browser gives the embedding page
 * no reliable way to detect that, so a page link always carries the new-tab
 * button and a one-line explanation, rather than a blank box and no way out.
 */
export const LinkViewerHost = ({ children }: { children?: ReactNode }) => {
    const { t } = useTranslation();
    const [link, setLink] = useState<ClassifiedLink | null>(null);

    useEffect(() => {
        const onClick = (event: MouseEvent) => {
            if (event.defaultPrevented || event.button !== 0) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const anchor = (event.target as Element | null)?.closest?.("a[data-link-viewer]") as HTMLAnchorElement | null;
            if (!anchor) return;
            const classified = classifyLink(anchor.href);
            if (!classified) return;
            event.preventDefault();
            setLink(classified);
            logEvent({ level: "INFO", component: "links", message: "link.viewer.opened", details: { kind: classified.kind } });
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);

    const close = useCallback(() => setLink(null), []);

    return (
        <>
            {children}
            {link && (
                <Dialog
                    isOpen
                    title={link.label}
                    onClose={close}
                    size="lg"
                    className={`link-viewer link-viewer--${link.kind}`}
                >
                    <div className="link-viewer__bar">
                        <span className="link-viewer__address" title={link.url}>
                            {link.url.startsWith("https://") && <span className="link-viewer__lock" aria-label="Secure site">🔒</span>}
                            <span className="link-viewer__url">{link.url}</span>
                        </span>
                        <a className="btn btn-ghost btn-compact" href={link.url} target="_blank" rel="noopener noreferrer">
                            {t("linkViewer.openNewTab")}
                        </a>
                    </div>
                    <LinkBody link={link} />
                    {(link.kind === "page" || link.kind === "pdf") && (
                        <p className="link-viewer__hint">{t("linkViewer.blankHint")}</p>
                    )}
                </Dialog>
            )}
        </>
    );
};

const LinkBody = ({ link }: { link: ClassifiedLink }) => {
    const { t } = useTranslation();
    const [broken, setBroken] = useState(false);

    if (link.kind === "image") {
        return broken ? (
            <p className="link-viewer__hint">{t("linkViewer.imageFailed")}</p>
        ) : (
            <img className="link-viewer__image" src={link.viewUrl} alt={link.label} referrerPolicy="no-referrer" onError={() => setBroken(true)} />
        );
    }
    return (
        <iframe
            className="link-viewer__frame"
            src={link.viewUrl}
            title={link.label}
            referrerPolicy="no-referrer"
            // No same-origin grant: the page runs, but can never reach this app's
            // storage or session, whatever it is.
            sandbox="allow-scripts allow-popups allow-forms allow-presentation"
            allow={link.kind === "youtube" ? "encrypted-media; picture-in-picture; fullscreen" : undefined}
            allowFullScreen={link.kind === "youtube"}
        />
    );
};
